import { isValidNumber, normalize, DEFAULT_BIG_NUDGE, DEFAULT_SMALL_NUDGE } from './formatter.utils.js';

/**
 * A formatter for numbers that formats them as strings, and can parse them back to numbers.
 * Accepts options for min and max, and also for how the number is formatted w.r.t. decimals.
 *
 * @note This formatter is designed to work with numbers. It can be extended to work with
 * other types as long as they are an intersection of `number` and something else. You will
 * have to override the `format` and likely `parse` methods to handle the additional type,
 * but can call `super` to handle the number formatting part.
 */
class NumberFormatter {
    opts;
    min = undefined;
    max = undefined;
    constructor(opts = {}) {
        this.opts = opts;
        this.min = opts.min;
        this.max = opts.max;
    }
    format(val) {
        // Even though we've typed this not to accept null or undefined, we never want to show
        // NaN or Infinity anywhere in the UI, so instead just return an empty string.
        if (!isValidNumber(val))
            return '';
        const { idealLength } = this.opts;
        const out = formatNumber(val, this.opts);
        if (!idealLength || out.length <= idealLength) {
            return out;
        }
        const decimalIndex = out.indexOf('.');
        // Nothing we can do to shorten the string.
        if (decimalIndex === -1)
            return out;
        const decimals = out.length - decimalIndex - 1;
        const min = this.opts.minimumFractionDigits ?? 0;
        // We already have the min number of decimals, so we can't shorten it.
        if (min === decimals)
            return out;
        const tooLongBy = out.length - idealLength;
        const idealDecimals = Math.max(min, decimals - tooLongBy);
        return formatNumber(val, {
            minimumFractionDigits: idealDecimals,
            maximumFractionDigits: idealDecimals,
        });
    }
    parse(value) {
        const parsed = parseFloat(normalize(value));
        if (!isValidNumber(parsed)) {
            throw new Error(`Invalid number: ${value}`);
        }
        // edge case: handle -0 (comparing to -0 is not only the same, it'll also show warnings in JS)
        if (parsed === 0)
            return 0;
        return parsed;
    }
    getNudgeAmount(big) {
        return big
            ? (this.opts.bigNudge ?? DEFAULT_BIG_NUDGE)
            : (this.opts.nudge ?? DEFAULT_SMALL_NUDGE);
    }
    incrementBy(val, amount, _incrementTargets) {
        return (val + amount);
    }
    normalize = normalize;
}
function formatNumber(value, opts) {
    const safeOpts = { ...opts, useGrouping: false };
    try {
        const fmt = new Intl.NumberFormat('en-US', {
            ...safeOpts,
            // A more recent property that typescript doesn't know about. If the formatted number is an
            // integer, it allows ignoring the min/max fraction digits setting above. This is purely
            // progressive enhancement, and for a percent based slider, announces 0 and 1 as "0" and "1"
            // instead of "0.00" and "1.00". If the browser doesn't support this it is silently ignored.
            // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#trailingzerodisplay
            ['trailingZeroDisplay']: 'stripIfInteger',
            // Never format as "-0". Passing "negative" is newer, which is why we try/catch and omit.
            // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#signdisplay
            ['signDisplay']: 'negative',
        });
        return fmt.format(value);
    }
    catch {
        const fmt = new Intl.NumberFormat('en-US', safeOpts);
        return fmt.format(value).replace(/\.0+$/, '');
    }
}

export { NumberFormatter };
//# sourceMappingURL=formatter.js.map
